import { UseQueryOptions, UseQueryResult, useQuery } from '@tanstack/react-query';

import { useApplicationContext } from 'shared/context/ApplicationContext';
import { useClientSideState } from 'shared/context/ServerSideStoreContext';
import { IDesktopV2MapGetClustersRequestSchema } from 'shared/repositories/commercial-listing-map/entities/request/DesktopV2MapGetClustersRequestSchema';
import { completeClustersJsonQuerySelector } from 'shared/store/clientSide/slices/jsonQuery/selectors/smart';
import { mapConfigurationSelector } from 'shared/store/clientSide/slices/mapConfiguration';
import { mapConfigurationBboxSelector } from 'shared/store/clientSide/slices/mapConfiguration/selectors/smart';
import { mapSelectedBboxSelector } from 'shared/store/clientSide/slices/mapControls/selectors/smart';
import { GetRealClustersResponse } from 'shared/types/Clusters';
import { getCancellationTokenFromSignal } from 'shared/utils/getCancellationTokenFromSignal';

import { QueryError, QueryKey, getQueryKey } from './internal/config';
import { fetchGetClustersService } from './internal/fetchService';

type UseGetClustersQueryOptions<TData> = UseQueryOptions<GetRealClustersResponse, QueryError, TData, QueryKey>;

export function useGetClustersWithParametersQuery<TData = GetRealClustersResponse>(
  parameters: IDesktopV2MapGetClustersRequestSchema,
  options?: UseGetClustersQueryOptions<TData>,
): UseQueryResult<TData, QueryError> {
  const applicationContext = useApplicationContext();

  return useQuery<GetRealClustersResponse, QueryError, TData, QueryKey>(
    getQueryKey(),
    ({ signal }) => {
      return fetchGetClustersService({
        applicationContext,
        cancellationToken: getCancellationTokenFromSignal(signal),
        parameters,
      });
    },
    { enabled: false, ...options },
  );
}

export function useGetClustersQuery<TData = GetRealClustersResponse>(
  options?: UseGetClustersQueryOptions<TData>,
): UseQueryResult<TData, QueryError> {
  const clientSideState = useClientSideState();
  const state = clientSideState.getState();

  const jsonQuery = completeClustersJsonQuerySelector(state);
  const bbox = mapConfigurationBboxSelector(state);
  const { zoom } = mapConfigurationSelector(state);
  const selectedBbox = mapSelectedBboxSelector(state);

  return useGetClustersWithParametersQuery({ bbox, jsonQuery, selectedBbox, zoom }, options);
}
